import { useMemo, useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ArrowDownWideNarrow, Flame, Activity, ChevronDown, Check, Plus, Shield } from 'lucide-react';
import { TEAMS, vitalsOf, type Vitals } from '@/lib/teams';
import type { Ticket } from '@/lib/mock';
import { HubTile } from './HubTile';
import { useRole } from '@/lib/role';
import { useNow } from '@/lib/useNow';
import { cn } from '@/lib/utils';
import { useOrganizationHierarchy } from '@/lib/useOrganizationHierarchy';
import { useUsers } from '@/lib/useUsers';

type Sort = 'attention' | 'load' | 'breach' | 'name';

export function OrgHub({
  tickets,
  onOpenTeam,
  onNew,
}: {
  tickets: Ticket[];
  onOpenTeam: (id: string) => void;
  onNew?: () => void;
}) {
  const navigate = useNavigate();
  const now = useNow(5000);
  const { role, teamId } = useRole();
  const [q, setQ] = useState('');
  const [sort, setSort] = useState<Sort>('attention');
  const [selectedOrgId, setSelectedOrgId] = useState<string>('all');
  const [selectedDepartment, setSelectedDepartment] = useState<string>('all');
  const { organizations } = useOrganizationHierarchy();

  const isGlobalAdmin = role === 'Super Admin' || role === 'Admin';
  
  const visibleTeams = useMemo(() => {
    let teams = isGlobalAdmin ? TEAMS : (teamId ? TEAMS.filter((t) => t.id === teamId) : TEAMS);
    if (selectedDepartment !== 'all') {
      teams = teams.filter(t => t.type === selectedDepartment);
    }
    return teams;
  }, [isGlobalAdmin, teamId, selectedDepartment]);
  
  const filteredTickets = useMemo(() => {
    if (selectedOrgId === 'all') return tickets;
    return tickets.filter(t => t.organizationId === selectedOrgId);
  }, [tickets, selectedOrgId]);

  const rows = useMemo(() => {
    const withVitals = visibleTeams.map((t) => {
      const baseVitals = vitalsOf(t, filteredTickets, now);
      return { team: t, vitals: baseVitals };
    });
    const rank = (v: Vitals) => (v.status === 'critical' ? 2 : v.status === 'warning' ? 1 : 0);
    const filtered = withVitals.filter((r) =>
      q ? r.team.name.toLowerCase().includes(q.toLowerCase()) : true,
    );
    filtered.sort((a, b) => {
      if (sort === 'load') return b.vitals.capacity - a.vitals.capacity;
      if (sort === 'breach') return b.vitals.breaching - a.vitals.breaching;
      if (sort === 'name') return a.team.name.localeCompare(b.team.name);
      // attention: critical/strained first, then by heat
      return rank(b.vitals) - rank(a.vitals) || b.vitals.heat - a.vitals.heat;
    });
    return filtered;
  }, [visibleTeams, filteredTickets, now, q, sort]);

  const attention = rows.filter((r) => r.vitals.status !== 'healthy').length;

  const stats = useMemo(() => {
    const total = filteredTickets.length;
    const open = filteredTickets.filter(t => !['Closed', 'Resolved'].includes(t.state)).length;
    const closed = filteredTickets.filter(t => ['Closed', 'Resolved'].includes(t.state)).length;
    const pending = filteredTickets.filter(t => t.state === 'Pending').length;
    const unassigned = filteredTickets.filter(t => t.state !== 'Closed' && t.state !== 'Resolved' && !t.assigneeId && !t.assignee).length;
    const assigned = open - unassigned;
    const highPriority = filteredTickets.filter(t => ['P1', 'P2', 'High', 'Urgent', 'Critical'].includes(t.priority) && !['Closed', 'Resolved'].includes(t.state)).length;
    const attachments = filteredTickets.reduce((acc, t) => acc + (t.attachments?.length || 0), 0);
    
    // Average resolution time
    const resolvedTickets = filteredTickets.filter(t => t.state === 'Resolved' || t.state === 'Closed');
    const resolutionTimes = resolvedTickets.map(t => {
      const resolvedEvent = t.events.slice().reverse().find(e => e.kind === 'state' && (e.label.includes('Resolved') || e.label.includes('Closed')));
      if (resolvedEvent && t.createdAt) {
        return resolvedEvent.at - t.createdAt;
      }
      return 0;
    }).filter(time => time > 0);
    const avgResolutionTimeMs = resolutionTimes.length > 0 ? resolutionTimes.reduce((a,b) => a+b, 0) / resolutionTimes.length : 0;
    const avgResolutionHours = avgResolutionTimeMs > 0 ? (avgResolutionTimeMs / (1000 * 60 * 60)).toFixed(1) : '0';

    // Technician performance
    const techStats: Record<string, { resolved: number, active: number }> = {};
    filteredTickets.forEach(t => {
      if (t.assigneeId) {
        if (!techStats[t.assigneeId]) techStats[t.assigneeId] = { resolved: 0, active: 0 };
        if (t.state === 'Resolved' || t.state === 'Closed') {
          techStats[t.assigneeId].resolved++;
        } else {
          techStats[t.assigneeId].active++;
        }
      }
    });

    const recentTickets = [...filteredTickets].sort((a,b) => b.createdAt - a.createdAt).slice(0, 5);
    const sentinelCount = filteredTickets.filter(t => t.sourceType === 'SENTINEL' || t.source === 'Sentinel').length;
    const userCreatedCount = filteredTickets.filter(t => t.sourceType === 'USER_CREATED' || (!t.sourceType && t.source !== 'Sentinel')).length;
    const adminCreatedCount = filteredTickets.filter(t => t.sourceType === 'ADMIN_CREATED').length;

    return { total, open, closed, pending, unassigned, assigned, highPriority, attachments, avgResolutionHours, techStats, recentTickets, sentinelCount, userCreatedCount, adminCreatedCount };
  }, [filteredTickets]);

  const { profiles } = useUsers();

  return (
    <div className="flex h-full flex-col">

      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-[1560px] px-6 py-6">
          {/* heading + controls */}
          <div className="flex flex-wrap items-center gap-4 mb-2 w-full">
            <div className="flex items-center gap-4">
              <h1 className="font-display text-2xl font-bold tracking-tight text-theme">
                {isGlobalAdmin ? 'Organization Overview' : 'My team'}
              </h1>
              <div className="hidden lg:flex items-center gap-1.5 text-sm font-bold text-muted">
                <span>{visibleTeams.length} team{visibleTeams.length === 1 ? '' : 's'}</span>
                <span>·</span>
                {attention > 0 ? (
                  <span className="text-red-400">{attention} need attention</span>
                ) : (
                  <span className="text-[var(--color-primary)]">all healthy</span>
                )}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 ml-auto lg:ml-4">
              {isGlobalAdmin && (
                <>
                  <OrgDropdown 
                    value={selectedOrgId} 
                    options={organizations.map(org => ({ id: org.id, name: org.organization_name }))} 
                    onChange={setSelectedOrgId}
                    allLabel="All Organizations"
                  />
                  <OrgDropdown 
                    value={selectedDepartment} 
                    options={[
                      { id: 'it', name: 'IT' },
                      { id: 'soc', name: 'Security' },
                      { id: 'dev', name: 'Development' },
                      { id: 'design', name: 'Design' },
                      { id: 'generic', name: 'General' },
                    ]} 
                    onChange={setSelectedDepartment} 
                    allLabel="All Departments"
                  />
                </>
              )}
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search teams…"
                  className="h-11 w-72 rounded-[1rem] neo-inset pl-11 pr-4 text-sm font-bold text-theme placeholder-muted-foreground outline-none transition-all focus:ring-2 focus:ring-[var(--color-primary)]/50 shadow-sm"
                />
              </div>
              <Seg
                value={sort}
                onChange={setSort}
                options={[
                  { id: 'attention', label: 'Attention', icon: Flame },
                  { id: 'load', label: 'Load', icon: ArrowDownWideNarrow },
                  { id: 'breach', label: 'Breach', icon: Activity },
                ]}
              />
            </div>
            <div className="ml-auto flex items-center gap-2">
              <button
                onClick={() => navigate('/admin/soc')}
                className="inline-flex h-11 items-center gap-2 rounded-2xl border border-primary/40 bg-primary/10 px-4 text-sm font-bold text-primary shadow-sm transition-all hover:bg-primary/20 active:scale-95"
                title="SOC Use Case Creation & Deployment Process (15 Steps)"
              >
                <Shield className="h-4 w-4" />
                <span className="hidden sm:inline">SOC Pipeline</span>
              </button>
              {onNew && (
                <button
                  onClick={onNew}
                  className="inline-flex h-11 items-center gap-2 rounded-2xl bg-[var(--color-primary)] px-5 text-sm font-bold text-white shadow-md transition-all hover:brightness-110 active:scale-95"
                >
                  <Plus className="h-4 w-4" />
                  New
                </button>
              )}
            </div>
          </div>


          {/* grid */}
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
            {rows.map(({ team, vitals }) => (
              <HubTile
                key={team.id}
                team={team}
                vitals={vitals}
                onOpen={() => onOpenTeam(team.slug)}
              />
            ))}
          </div>

          {rows.length === 0 && (
            <div className="mt-12 flex flex-col items-center justify-center p-12 text-center rounded-2xl neo border border-dashed border-border/60">
              <Activity className="h-8 w-8 text-muted mb-3 opacity-60" />
              <h3 className="text-base font-semibold text-theme">No Teams Available</h3>
              <p className="text-sm text-muted-foreground mt-1 max-w-sm">
                {q ? `No teams match "${q}". Try clearing your search filter.` : 'No teams match the current filter selection.'}
              </p>
            </div>
          )}

          {isGlobalAdmin && (
            <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
              <div className="col-span-1 flex flex-col neo rounded-[2rem] p-5">
                <h2 className="text-sm font-bold text-theme mb-4">Ticket Resolution Time</h2>
                <div className="flex-1 flex flex-col items-center justify-center">
                  <span className="text-5xl font-display font-bold text-[var(--color-primary)]">{stats.avgResolutionHours}</span>
                  <span className="text-muted-foreground mt-2 text-sm uppercase tracking-wider font-medium">Average Hours</span>
                </div>
              </div>

              <div className="col-span-1 flex flex-col neo rounded-[2rem] p-5">
                <h2 className="text-sm font-bold text-theme mb-4">Ticket Sources</h2>
                <div className="flex-1 flex flex-col justify-center space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-muted-foreground">User Portal</span>
                    <span className="bg-emerald-500/10 text-emerald-500 border border-emerald-500/20 px-2 py-0.5 rounded-full text-xs font-bold">{stats.userCreatedCount}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-muted-foreground">MS Sentinel</span>
                    <span className="bg-indigo-500/10 text-indigo-500 border border-indigo-500/20 px-2 py-0.5 rounded-full text-xs font-bold">{stats.sentinelCount}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-muted-foreground">Admin / Staff</span>
                    <span className="bg-amber-500/10 text-amber-500 border border-amber-500/20 px-2 py-0.5 rounded-full text-xs font-bold">{stats.adminCreatedCount}</span>
                  </div>
                </div>
              </div>
              
              <div className="col-span-1 flex flex-col neo rounded-[2rem] p-5">
                <h2 className="text-sm font-bold text-theme mb-4">Technician Performance</h2>
                <div className="flex-1 overflow-y-auto space-y-3 pr-2">
                  {Object.entries(stats.techStats).sort((a,b) => b[1].resolved - a[1].resolved).slice(0, 5).map(([techId, data]) => {
                    const profile = profiles.find(p => p.id === techId);
                    return (
                      <div key={techId} className="flex items-center justify-between">
                        <span className="text-sm font-medium text-theme truncate mr-2">{profile?.full_name || profile?.email || techId}</span>
                        <div className="flex gap-2 text-xs">
                          <span className="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 px-2 py-0.5 rounded font-medium">{data.resolved} resolved</span>
                          <span className="bg-amber-500/10 text-amber-600 dark:text-amber-400 px-2 py-0.5 rounded font-medium">{data.active} active</span>
                        </div>
                      </div>
                    );
                  })}
                  {Object.keys(stats.techStats).length === 0 && (
                    <div className="text-sm text-muted-foreground text-center py-4">No data available</div>
                  )}
                </div>
              </div>

              <div className="col-span-1 flex flex-col neo rounded-[2rem] p-5">
                <h2 className="text-sm font-bold text-theme mb-4">Recent Tickets</h2>
                <div className="flex-1 overflow-y-auto space-y-3 pr-2">
                  {stats.recentTickets.map(t => (
                    <div key={t.id} className="flex items-center justify-between border-b border-border/50 pb-2 last:border-0 last:pb-0">
                      <div className="flex flex-col min-w-0">
                        <span className="text-sm font-medium text-theme truncate">{t.subject}</span>
                        <span className="text-[11px] text-muted-foreground font-mono mt-0.5">{t.ref} · {t.state}</span>
                      </div>
                      <span className={cn("text-[10px] uppercase px-1.5 py-0.5 rounded-sm font-bold shrink-0 ml-2", t.priority.includes('1') || t.priority.includes('2') || t.priority === 'P1' ? 'bg-red-500/10 text-red-500' : 'bg-muted text-muted-foreground')}>{t.priority}</span>
                    </div>
                  ))}
                  {stats.recentTickets.length === 0 && (
                    <div className="text-sm text-muted-foreground text-center py-4">No recent tickets</div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Seg<T extends string>({
  value,
  onChange,
  options,
}: {
  value: T;
  onChange: (v: T) => void;
  options: { id: T; label: string; icon: typeof Flame }[];
}) {
  return (
    <div className="flex items-center gap-1 neo rounded-[1.25rem] p-1.5">
      {options.map((o) => (
        <button
          key={o.id}
          onClick={() => onChange(o.id)}
          className={cn(
            'inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-bold transition-all duration-200',
            value === o.id ? 'bg-[var(--color-primary)] text-white shadow-md' : 'text-muted-foreground hover:text-theme neo-interactive',
          )}
        >
          <o.icon className="h-4 w-4" /> {o.label}
        </button>
      ))}
    </div>
  );
}

function OrgDropdown({ 
  value, 
  options, 
  onChange,
  allLabel = 'All Options'
}: { 
  value: string; 
  options: { id: string; name: string }[]; 
  onChange: (id: string) => void;
  allLabel?: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const click = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', click);
    return () => document.removeEventListener('mousedown', click);
  }, []);

  const selected = options.find(o => o.id === value) || { id: 'all', name: allLabel };

  return (
    <div ref={ref} className="relative inline-block text-left">
      <button 
        onClick={() => setOpen(!open)}
        className="flex h-11 items-center justify-between gap-2 rounded-2xl border border-white/40 dark:border-white/10 bg-white/40 dark:bg-black/20 px-4 text-sm font-bold text-theme outline-none transition-all hover:bg-white/60 dark:hover:bg-black/40 focus:ring-2 focus:ring-[var(--color-primary)]/50 shadow-sm min-w-[180px]"
      >
        <span className="truncate">{selected.name}</span>
        <ChevronDown className="h-4 w-4 opacity-50" />
      </button>

      {open && (
        <div className="absolute left-0 top-full mt-2 z-50 w-full min-w-[200px] overflow-hidden rounded-xl border border-[rgba(255,255,255,0.05)] bg-[var(--color-bg)] shadow-xl neo p-1">
          <div className="max-h-64 overflow-y-auto">
            <button
              onClick={() => {
                onChange('all');
                setOpen(false);
              }}
              className={cn(
                "flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm font-medium transition-colors",
                value === 'all' ? "bg-[var(--color-primary)]/10 text-[var(--color-primary)]" : "text-theme hover:bg-black/5 dark:hover:bg-white/5"
              )}
            >
              {allLabel}
              {value === 'all' && <Check className="h-4 w-4" />}
            </button>
            {options.map((opt) => (
              <button
                key={opt.id}
                onClick={() => {
                  onChange(opt.id);
                  setOpen(false);
                }}
                className={cn(
                  "flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm font-medium transition-colors",
                  value === opt.id ? "bg-[var(--color-primary)]/10 text-[var(--color-primary)]" : "text-theme hover:bg-black/5 dark:hover:bg-white/5"
                )}
              >
                {opt.name}
                {value === opt.id && <Check className="h-4 w-4" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
